import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { mdiAlertCircleOutline, mdiFilterVariant } from "@mdi/js";
import Icon from "@mdi/react";
import { useToggle } from "usehooks-ts";

import truckEmpty from "assets/images/truck-empty.png";
import Button from "components/Button";
import CustomMap from "components/CustomMap";
import DataTable from "components/DataTable";
import Header from "components/Header";
import Modal from "components/Modal";
import SubHeaderWithFilter from "components/SubHeaderWithFilter";
import TipModal from "components/TipModal";
import { routes } from "constants/routes";
import {
  TRUCK_FILTER_ITEMS,
  TRUCK_SEARCH_EXPANDABLE_HEADER_LIST,
  TRUCK_SEARCH_HEADER_LIST
} from "constants/trucks";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import MainLayout from "layouts/Main";
import { ITruckData } from "models/Truck";
import LoadingSpinner from "../../../../components/LoadingSpinner";
import usePaginatedQuery from "../../../../hooks/usePaginatedQuery";
import TruckItem from "../modules/Item";
import TruckFilterForm from "../modules/TruckFilterForm";
import { QUERY_TRUCK_LIST } from "./query";
import styles from "./style.module.scss";

export default function TruckSearch() {
  const navigate = useNavigate();
  const { isDesktop } = useDeviceType();

  const [isScroll, setIsScroll] = useState(false);
  const [isFilterModalOpen, toggleFilterModal] = useToggle();
  const [isTipModalOpen, toggleTipModal] = useToggle();
  const [viewType, setViewType] = useState("list");
  const [filter, setFilter] = useState<Record<string, any>>({
    orderBy: "-time_posted"
  });
  const [isHideDesktopFilter, setIsHideDesktopFilter] = useState(true);
  const ContainerRef = useRef<HTMLDivElement>(null);

  const {
    data,
    loading,
    error,
    loadNextPage,
    pageInfo,
    totalCount,
    goToPage,
    loadPreviousPage,
    setPageSize,
    resetPagination
  } = usePaginatedQuery<any, ITruckData>({
    query: QUERY_TRUCK_LIST,
    variables: filter,
    cursorField: "allTrucks",
    pageSize: 20,
    loadMore: true
  });
  useEffect(() => {
    if (filter?.orderBy !== "-time_posted" && isScroll) {
      setIsScroll(false);
    }
  }, [isScroll, filter?.orderBy]);

  useEffect(() => {
    if (error) {
      showErrorMessage(error.message);
    }
  }, [loading, error]);

  const mapPoints = useMemo(() => {
    return data
      .filter((truck) => truck.originCoords)
      .map(({ originCoords }) => ({
        lat: originCoords.latitude,
        lng: originCoords.longitude
      }));
  }, [JSON.stringify(data)]);

  function goDetails(id: string) {
    navigate(`${routes.TRUCKS_SEARCH}/${id}`);
  }

  const onScroll = async () => {
    if (ContainerRef.current) {
      const { scrollTop, offsetHeight, scrollHeight } = ContainerRef.current;
      if (!!scrollTop && scrollTop + offsetHeight + 1 >= scrollHeight && pageInfo.hasNextPage) {
        setIsScroll(true);
        loadNextPage();
      }
    }
  };

  return (
    <MainLayout
      desktopTitle="Truck Search"
      customHeader={<Header label="Truck Search" goBackPath={routes.TRUCKS} isHideDesktop />}
      className="pl-3.5 pr-3.5 flex-1 overflow-auto">
      <div className={styles.truckSearch}>
        {isDesktop ? (
          <button type="button" className={styles.proTipButton} onClick={toggleTipModal}>
            <Icon path={mdiAlertCircleOutline} />
            <span className="underline">ProTip</span>
          </button>
        ) : null}

        {isDesktop && isHideDesktopFilter ? (
          <TruckFilterForm
            loading={loading}
            setFilter={setFilter}
            setIsScroll={setIsScroll}
            toggleFilterModal={toggleFilterModal}
            resetPagination={resetPagination}
          />
        ) : null}

        <SubHeaderWithFilter
          viewType={viewType}
          onViewTypeChange={setViewType}
          setFilter={setFilter}
          filter={filter}
          setIsHideDesktopFilter={setIsHideDesktopFilter}
          filterOptions={TRUCK_FILTER_ITEMS}
          toggleTipModal={toggleTipModal}
        />

        <div className={styles.truckSearchBody} ref={ContainerRef} onScroll={onScroll}>
          {viewType === "list" ? (
            <>
              {isDesktop ? (
                <DataTable
                  headers={TRUCK_SEARCH_HEADER_LIST}
                  data={data.map((truck) => ({
                    ...truck,
                    truckId: truck.id,
                    originData: {
                      city: truck.originCity,
                      region: truck.originRegion
                    },
                    destinationData: {
                      city: truck.destinationCity,
                      region: truck.destinationRegion
                    },
                    details: { action: () => goDetails(truck.id) }
                  }))}
                  sortDisableList={["details"]}
                  totalCount={totalCount}
                  nextLink={pageInfo.hasNextPage}
                  prevLink={pageInfo.hasPreviousPage}
                  setPageSize={setPageSize}
                  goToPage={goToPage}
                  fetchPrevPage={loadPreviousPage}
                  fetchNextPage={loadNextPage}
                  loading={loading}
                  ExpandableView={({ row }: { row: ITruckData }) => (
                    <DataTable headers={TRUCK_SEARCH_EXPANDABLE_HEADER_LIST} data={[row]} simple />
                  )}
                />
              ) : (
                <ul className={styles.truckList}>
                  {data.length ? (
                    data.map((item) => (
                      <TruckItem key={item.id} data={item} onClickDetails={goDetails} />
                    ))
                  ) : loading ? (
                    <LoadingSpinner isFetching={loading} />
                  ) : (
                    <div className={styles.truckEmptyList}>
                      <img src={truckEmpty} alt="Empty truck list" />
                      <h3 className={styles.truckEmptyTitle}>No results found.</h3>
                      <p className="font-medium text-center">
                        Try refining your search to view results.
                      </p>
                    </div>
                  )}
                </ul>
              )}
            </>
          ) : (
            <div className={styles.truckMap}>
              <CustomMap points={mapPoints} center={mapPoints[0]} />
            </div>
          )}
        </div>

        {!isDesktop ? (
          <Button
            onClick={toggleFilterModal}
            className={styles.truckSearchRoundButton}
            rounded
            iconButton>
            <Icon path={mdiFilterVariant} className="m-auto" />
          </Button>
        ) : null}

        <Modal
          isOpen={isFilterModalOpen}
          closeModal={toggleFilterModal}
          wrapperClass="!pt-0"
          dialogClass="!bg-foreground !pt-6 !mt-20"
          closeClass="!top-14">
          <TruckFilterForm
            loading={loading}
            setFilter={setFilter}
            setIsScroll={setIsScroll}
            toggleFilterModal={toggleFilterModal}
            resetPagination={resetPagination}
          />
        </Modal>

        <TipModal
          isModalOpen={isTipModalOpen}
          toggleModal={toggleTipModal}
          title="Truck Search"
          content={
            <div className="px-2 pb-5 text-xs desktop:text-sm desktop:pb-7">
              <p className="py-2">
                Enter multiple states on pick up and delivery based on your ongoing logistics needs
                to find trucks posted or interested in hauling in larger regions so you get more
                results.
              </p>
              <p className="py-2">
                You can also extend out the end time for the search to reveal even more potential
                carrier matches for current and upcoming lanes.
              </p>
            </div>
          }
        />
      </div>
    </MainLayout>
  );
}
